/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  RoutineFilterColumns,
  RoutineFilterModel,
  useCurrentWorkspace,
  useRoutineFilter,
  useSetRoutineFilter,
  useValidateRoutineListOrder,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  ColumnSelection,
  DropdownFooter,
  FilterType,
  FormContext,
  filterStyles as styles,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';

type RoutineFilterProps = {
  onCloseSideBar: () => void;
  type: FilterType;
};

export const RoutineFilter = (props: RoutineFilterProps) => {
  const { data: routineFilter } = useRoutineFilter();
  const { mutateAsync: setRoutineFilter } = useSetRoutineFilter();
  const validateListOrder = useValidateRoutineListOrder();
  const currentWorkspace = useCurrentWorkspace();

  const formSchema = yupObject({
    columns: array().of(string().optional().nullable()).optional().nullable(),
  });
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      columns: routineFilter?.columns,
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    reset({
      columns: routineFilter?.columns,
    });
  }, [routineFilter, reset]);

  const selectedColumns = watch('columns');

  const isColumnsFilter = props.type === FilterType.ROUTINECOLUMNS;

  // Remove assetsCount from filter for OEM workspaces
  useEffect(() => {
    if (!currentWorkspace || !isColumnsFilter) return;

    const isProducerWorkspace =
      currentWorkspace.type === WorkspaceType.Producer;
    if (
      !isProducerWorkspace &&
      routineFilter?.columns?.includes('assetsCount')
    ) {
      const updatedColumns = routineFilter.columns.filter(
        (column: string) => column !== 'assetsCount'
      );
      setRoutineFilter(
        new RoutineFilterModel({
          ...routineFilter,
          columns: updatedColumns,
        })
      );
    }
  }, [currentWorkspace, isColumnsFilter, routineFilter, setRoutineFilter]);

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: new RoutineFilterModel().columns }),
    });
  }, [isColumnsFilter, reset, selectedColumns]);

  const canReset = (): boolean => {
    return !Object.values(getValues()).every((value) => value === undefined);
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setRoutineFilter(filterModel),
      validateListOrder(filterModel),
    ]);

    props.onCloseSideBar();
  };

  const onReset = async () => {
    const model = new RoutineFilterModel();
    const updatedFields = {
      ...(isColumnsFilter && { columns: model.columns }),
    };

    resetForm();

    const mergedFilters = { ...routineFilter, ...updatedFields };
    await applyFilter(new RoutineFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    const model = new RoutineFilterModel({
      ...routineFilter,
      ...data,
    });
    await applyFilter(model);
  };

  const renderStandardData = () => {
    const isProducerWorkspace =
      currentWorkspace?.type === WorkspaceType.Producer;
    const availableColumns = isProducerWorkspace
      ? RoutineFilterColumns.filter(
          (column: string) => column !== 'addonsCount'
        )
      : RoutineFilterColumns.filter(
          (column: string) => column !== 'assetsCount'
        );

    return (
      <ColumnSelection
        baseColumns={availableColumns}
        adminColumns={['workspace']}
        i18nPrefix="routine.filter"
      />
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { marginBottom: 30 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>
        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
